import type { Chat, ChatMessage } from '@/api/ChatApi';
import { showChat, storeChat } from '@/api/ChatApi';
import { rateChatMessage, streamChatMessage } from '@/api/ChatMessageApi';

// Shared state for the AI chat dialog
const isOpen = ref(false);
const chat = ref<Chat | null>(null);
const lastChatId = ref<number | null>(null);
const messages = ref<ChatMessage[]>([]);
const userInput = ref('');
const messagesEnd = ref<HTMLElement | null>(null);
const messagesContainer = ref<HTMLElement | null>(null);
const composeInput = ref<{ focusTextarea: () => void } | null>(null);
const messageLoading = ref(false);
const chatLoading = ref(false);
const initialMessage = ref<string>('');

export function useAiChat() {
  // Check for chat parameter in URL on mount
  onMounted(() => {
    checkForChatParameter();
  });

  function checkForChatParameter() {
    const url = new URL(window.location.href);
    const chatId = url.searchParams.get('chat');

    if (chatId && !isOpen.value) {
      openChat(parseInt(chatId));
    }
  }

  function addChatToUrl(chatId: number) {
    const url = new URL(window.location.href);
    url.searchParams.set('chat', chatId.toString());

    router.navigate({
      url: url.pathname + url.search,
      preserveState: true,
      replace: true
    });
  }

  function removeChatFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('chat');

    router.navigate({
      url: url.pathname + url.search,
      preserveState: true,
      replace: true
    });
  }

  function setupChatDialog(chatData: Chat | null, message?: string) {
    chat.value = chatData;
    if (chatData?.id) {
      lastChatId.value = chatData.id;
      addChatToUrl(chatData.id);
    }
    messages.value = [...(chatData?.messages ?? [])];
    initialMessage.value = message ?? '';
    userInput.value = message ?? '';
    isOpen.value = true;

    nextTick(() => {
      if (message) {
        composeInput.value?.focusTextarea();
      }
    });
  }

  function createChat(message?: string) {
    setupChatDialog(null, message);
  }

  async function openChat(chatId: number) {
    try {
      chatLoading.value = true;
      setupChatDialog(null);
      const { chat: chatData } = await showChat(chatId);
      setupChatDialog(chatData);
      chatLoading.value = false;
    } catch (error) {
      console.error('Error loading chat:', error);
      setupChatDialog(null);
    }
  }

  async function openChatDialog(message?: string) {
    // If dialog is already open, don't do anything
    if (isOpen.value) {
      return;
    }

    // If we have an existing chat and no new message, reopen it
    if (chat.value && !message) {
      isOpen.value = true;
      nextTick(() => {
        composeInput.value?.focusTextarea();
      });
      return;
    }

    // If we have a last chat ID and no new message, reopen the previous chat
    if (lastChatId.value && !message) {
      await openChat(lastChatId.value);
      return;
    }

    // Otherwise, create a new chat with the message
    createChat(message);
  }

  function hideChat() {
    isOpen.value = false;
    removeChatFromUrl();
  }

  function scrollToBottom(behavior?: ScrollBehavior) {
    nextTick(() => {
      messagesEnd.value?.scrollIntoView({ behavior: behavior ?? 'smooth' });
    });
  }

  watch(
    messages,
    () => {
      scrollToBottom('instant');
    },
    { deep: true }
  );

  async function rateMessage(message: ChatMessage, rating: number) {
    if (!message.id) {
      return;
    }

    try {
      const { message: updatedMessage } = await rateChatMessage(message.id, rating);
      message.rating = updatedMessage.rating;
    } catch (error) {
      console.error('Error rating message:', error);
    }
  }

  async function streamResponseToChat(chatToSendTo: Chat, userMessage: string) {
    // Create placeholder assistant message for streaming
    const assistantMessage = reactive<ChatMessage>({
      type: 'assistant',
      content: '',
      rating: 0
    });
    messages.value.push(assistantMessage);

    try {
      const response = await streamChatMessage(
        chatToSendTo.id,
        userMessage,
        'student-adviser-chat'
      );
      const stream = response.data;

      // consume response
      const reader = stream.pipeThrough(new TextDecoderStream()).getReader();
      let firstChunkReceived = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        // value is decoded text chunk
        const lines = value.split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) {
            continue;
          }

          // Do NOT trim here; leading spaces are meaningful (tokens often start with a space)
          const data = line.slice(6);

          if (data === '[DONE]') {
            reader.cancel();
            return;
          }

          // Set loading to false as soon as we get the first chunk
          if (!firstChunkReceived) {
            messageLoading.value = false;
            firstChunkReceived = true;
          }

          assistantMessage.content += JSON.parse(data);
        }
      }
    } catch (e) {
      console.error(e);
      assistantMessage.content = 'Error communicating with server.';
      messageLoading.value = false;
    }
  }

  async function send() {
    const userMessage = userInput.value.trim();
    if (!userMessage || messageLoading.value) {
      return;
    }

    // Add the user message to UI immediately
    messages.value.push({ type: 'user', content: userMessage, rating: 0 });
    messageLoading.value = true;
    userInput.value = '';

    try {
      // Create chat if it doesn't exist
      if (!chat.value) {
        const newChat = await storeChat();

        chat.value = newChat.chat;
        lastChatId.value = chat.value.id;
        addChatToUrl(chat.value.id);
      }

      await streamResponseToChat(chat.value, userMessage);
    } catch (error) {
      console.error('Error sending message:', error);
      messageLoading.value = false;

      // If there's an assistant message being streamed, show error
      const lastMessage = messages.value[messages.value.length - 1];
      if (lastMessage?.type === 'assistant' && !lastMessage.content) {
        lastMessage.content = 'Sorry, there was an error processing your message.';
      }

      // Try to reload chat if it exists (in case some messages were processed before error)
      if (chat.value) {
        try {
          await router.reload();
        } catch (reloadError) {
          console.error('Error reloading chat after error:', reloadError);
        }
      }
    }
  }

  return {
    // State
    isOpen: readonly(isOpen),
    chat: readonly(chat),
    messages,
    userInput,
    messagesEnd,
    messagesContainer,
    composeInput,
    messageLoading: readonly(messageLoading),
    chatLoading: readonly(chatLoading),
    initialMessage: readonly(initialMessage),

    // Methods
    createChat,
    openChat,
    openChatDialog,
    hideChat,
    send,
    rateMessage
  };
}
